import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getCannedMessages,
    deleteCannedMessage,
    RESET as RESET_MESSAGES
} from '../../redux/features/cannedMessages/cannedMessageSlice';
import {
    getCategories,
    deleteCategory,
    RESET as RESET_CATEGORIES
} from '../../redux/features/categories/categorySlice';
import CannedMessageModal from './CannedMessageModal';
import CategoryModal from './CategoryModal';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import './CannedMessagesPage.css';

const CannedMessagesPage = () => {
    const dispatch = useDispatch();
    const { cannedMessages, isLoading: messagesLoading } = useSelector((state) => state.cannedMessages);
    const { categories, isLoading: categoriesLoading } = useSelector((state) => state.categories);

    const [activeTab, setActiveTab] = useState('categories');
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [editingMessage, setEditingMessage] = useState(null);
    const [editingCategory, setEditingCategory] = useState(null);
    const [filterCategory, setFilterCategory] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        dispatch(getCategories());
        dispatch(getCannedMessages());
        return () => {
            dispatch(RESET_MESSAGES());
            dispatch(RESET_CATEGORIES());
        };
    }, [dispatch]);

    const handleCreateMessage = () => {
        setEditingMessage(null);
        setShowMessageModal(true);
    };

    const handleEditMessage = (message) => {
        setEditingMessage(message);
        setShowMessageModal(true);
    };

    const handleDeleteMessage = (id) => {
        confirmAlert({
            title: 'Delete Canned Message',
            message: 'Are you sure you want to delete this message?',
            buttons: [
                {
                    label: 'Delete',
                    onClick: () => dispatch(deleteCannedMessage(id))
                },
                {
                    label: 'Cancel'
                }
            ]
        });
    };

    const handleCreateCategory = () => {
        setEditingCategory(null);
        setShowCategoryModal(true);
    };

    const handleEditCategory = (category) => {
        setEditingCategory(category);
        setShowCategoryModal(true);
    };

    const handleDeleteCategory = (id, messageCount) => {
        if (messageCount > 0) {
            confirmAlert({
                title: 'Cannot Delete Category',
                message: `This category has ${messageCount} message(s) linked to it. Please reassign or delete those messages first.`,
                buttons: [{ label: 'OK' }]
            });
            return;
        }

        confirmAlert({
            title: 'Delete Category',
            message: 'Are you sure you want to delete this category?',
            buttons: [
                {
                    label: 'Delete',
                    onClick: () => dispatch(deleteCategory(id))
                },
                {
                    label: 'Cancel'
                }
            ]
        });
    };

    const handleCloseMessageModal = () => {
        setShowMessageModal(false);
        setEditingMessage(null);
    };

    const handleCloseCategoryModal = () => {
        setShowCategoryModal(false);
        setEditingCategory(null);
    };

    // Filter messages
    const filteredMessages = cannedMessages.filter(msg => {
        const matchesCategory = !filterCategory || (msg.category && msg.category._id === filterCategory);
        const matchesSearch = !searchTerm ||
            msg.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            msg.description.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    // Group messages by category
    const groupedMessages = filteredMessages.reduce((acc, msg) => {
        if (msg.category) {
            const catId = msg.category._id;
            if (!acc[catId]) {
                acc[catId] = {
                    category: msg.category,
                    messages: []
                };
            }
            acc[catId].messages.push(msg);
        }
        return acc;
    }, {});

    return (
        <div className="canned-messages-page">
            <div className="canned-page-header">
                <h2>Canned Messages Management</h2>
                <div className="header-actions">
                    {activeTab === 'categories' ? (
                        <button className="btn-create" onClick={handleCreateCategory}>
                            <i className="fa fa-plus"></i> Create Category
                        </button>
                    ) : (
                        <button className="btn-create" onClick={handleCreateMessage}>
                            <i className="fa fa-plus"></i> Create Message
                        </button>
                    )}
                </div>
            </div>

            <div className="tabs-container">
                <div className="tabs">
                    <button
                        className={`tab ${activeTab === 'categories' ? 'active' : ''}`}
                        onClick={() => setActiveTab('categories')}
                    >
                        <i className="fa fa-folder"></i> Categories ({categories.length})
                    </button>
                    <button
                        className={`tab ${activeTab === 'messages' ? 'active' : ''}`}
                        onClick={() => setActiveTab('messages')}
                    >
                        <i className="fa fa-comment"></i> Messages ({cannedMessages.length})
                    </button>
                </div>
            </div>

            {activeTab === 'categories' ? (
                <div className="canned-content">
                    {categoriesLoading ? (
                        <div className="loading-state">Loading categories...</div>
                    ) : categories.length === 0 ? (
                        <div className="empty-state">
                            <i className="fa fa-folder-o"></i>
                            <p>No categories found</p>
                            <button className="btn-create" onClick={handleCreateCategory}>
                                Create Your First Category
                            </button>
                        </div>
                    ) : (
                        <div className="categories-grid">
                            {categories.map(cat => (
                                <div key={cat._id} className="category-card">
                                    <div className="category-card-header">
                                        <div className="category-info">
                                            <div
                                                className="category-color-dot"
                                                style={{ backgroundColor: cat.color }}
                                            ></div>
                                            <div>
                                                <h4>{cat.title}</h4>
                                                {cat.description && (
                                                    <p className="category-desc">{cat.description}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="category-actions">
                                            <button onClick={() => handleEditCategory(cat)} title="Edit">
                                                <i className="fa fa-edit"></i>
                                            </button>
                                            <button
                                                onClick={() => handleDeleteCategory(cat._id, cat.messageCount)}
                                                title="Delete"
                                                className="delete-btn"
                                            >
                                                <i className="fa fa-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="category-card-footer">
                                        <span className="message-count">
                                            <i className="fa fa-comment-o"></i> {cat.messageCount || 0} message(s)
                                        </span>
                                        <span className={`status-badge ${cat.status}`}>
                                            {cat.status === 'active' ? (
                                                <><i className="fa fa-check-circle"></i> Active</>
                                            ) : (
                                                <><i className="fa fa-ban"></i> Inactive</>
                                            )}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <>
                    <div className="canned-filters">
                        <input
                            type="text"
                            placeholder="Search messages..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className="category-filter"
                        >
                            <option value="">All Categories</option>
                            {categories.map(cat => (
                                <option key={cat._id} value={cat._id}>{cat.title}</option>
                            ))}
                        </select>
                    </div>

                    <div className="canned-content">
                        {messagesLoading ? (
                            <div className="loading-state">Loading messages...</div>
                        ) : Object.keys(groupedMessages).length === 0 ? (
                            <div className="empty-state">
                                <i className="fa fa-file-text-o"></i>
                                <p>No messages found</p>
                                <button className="btn-create" onClick={handleCreateMessage}>
                                    Create Your First Message
                                </button>
                            </div>
                        ) : (
                            Object.keys(groupedMessages).map(catId => {
                                const { category, messages } = groupedMessages[catId];
                                return (
                                    <div key={catId} className="category-section">
                                        <h3 className="category-title" style={{ borderLeftColor: category.color }}>
                                            {category.title}
                                            <span className="count">({messages.length})</span>
                                        </h3>
                                        <div className="messages-table">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: '25%' }}>Title</th>
                                                        <th style={{ width: '50%' }}>Description</th>
                                                        <th style={{ width: '10%' }}>Status</th>
                                                        <th style={{ width: '15%', textAlign: 'right' }}>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {messages.map(msg => (
                                                        <tr key={msg._id}>
                                                            <td>
                                                                <strong>{msg.title}</strong>
                                                            </td>
                                                            <td className="description-cell">
                                                                {msg.description.length > 80
                                                                    ? `${msg.description.substring(0, 80)}...`
                                                                    : msg.description}
                                                            </td>
                                                            <td>
                                                                <span className={`status-badge-small ${msg.status}`}>
                                                                    {msg.status === 'active' ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </td>
                                                            <td className="actions-cell">
                                                                <button onClick={() => handleEditMessage(msg)} title="Edit" className="action-btn">
                                                                    <i className="fa fa-edit"></i>
                                                                </button>
                                                                <button onClick={() => handleDeleteMessage(msg._id)} title="Delete" className="action-btn delete-btn">
                                                                    <i className="fa fa-trash"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </>
            )}

            {showMessageModal && (
                <CannedMessageModal
                    message={editingMessage}
                    categories={categories}
                    onClose={handleCloseMessageModal}
                />
            )}

            {showCategoryModal && (
                <CategoryModal
                    category={editingCategory}
                    onClose={handleCloseCategoryModal}
                />
            )}
        </div>
    );
};

export default CannedMessagesPage;
